/**
 * Client-side RMD display helpers.
 * These are lightweight utilities for UI display only.
 * All actual RMD calculations happen server-side.
 *
 * All functions accept birth/death years (integers) rather than full Date
 * objects to avoid storing PII (full date of birth). The RMD calculation
 * engine only needs the year component.
 */

export function ageInYear(birthYear: number, year: number): number {
  return year - birthYear;
}

export function getRMDStartAge(birthYear: number): number {
  if (birthYear < 1951) return 72;
  if (birthYear <= 1959) return 73;
  return 75;
}

export function getRMDStartYear(birthYear: number): number {
  return birthYear + getRMDStartAge(birthYear);
}

// Annual QCD limits — update when IRS publishes inflation-adjusted limits.
const QCD_LIMITS: [number, number][] = [
  [2026, 110_000],
  [2025, 108_000],
  [2024, 105_000],
];

export function getQCDLimit(year: number): number {
  for (const [fromYear, limit] of QCD_LIMITS) {
    if (year >= fromYear) return limit;
  }
  return 100_000;
}

export type RuleResult = {
  ruleId: string;
  table: string;
  description: string;
  requiresAnnualDistribution: boolean;
  deadlineYear: number | null;
};

export type RuleError = {
  error: string;
};

export function isRuleResult(r: RuleResult | RuleError): r is RuleResult {
  return "ruleId" in r;
}

export function getRuleDisplayLabel(rule: RuleResult | RuleError): string {
  if (!isRuleResult(rule)) return "Manual entry required";

  switch (rule.ruleId) {
    case "OWN_UNIFORM":
    case "SPOUSE_AS_OWN":
      return "Uniform Lifetime Table";
    case "SPOUSE_BENEFICIARY":
    case "EDB_STRETCH":
    case "STRETCH_PRE_SECURE":
      return "Single Life Table";
    case "TEN_YEAR_WITH_ANNUAL":
      return "Single Life Table (10-year rule)";
    case "MINOR_CHILD":
      return "Single Life Table, then 10-year rule";
    case "FIVE_YEAR":
      return "5-year rule";
    case "TEN_YEAR_NO_ANNUAL":
      return "Ten-year rule";
    default:
      return rule.description;
  }
}

/** The label of a rule id; "Manual entry required" while the id is `null`. */
export function getRuleLabelForId(ruleId: string | null): string {
  if (ruleId == null) return getRuleDisplayLabel({ error: "no rule" });
  return getRuleDisplayLabel({
    ruleId,
    table: "",
    description: ruleId,
    requiresAnnualDistribution: false,
    deadlineYear: null,
  });
}

/**
 * Simplified rule determination for display purposes only.
 * Returns the ruleId and description — no calculations.
 */
export function determineRule(settings: {
  isInherited: boolean;
  ownerBirthYear: number | null;
  spouseIsSoleBeneficiary: boolean;
  spouseBirthYear: number | null;
  originalOwnerBirthYear?: number | null;
  originalOwnerDeathYear?: number | null;
  beneficiaryType?: string | null;
  spouseElection?: string | null;
  beneficiaryBirthYear?: number | null;
}): RuleResult | RuleError {
  if (!settings.isInherited) {
    if (settings.ownerBirthYear == null) {
      return {
        error:
          "Profile is missing owner birth year — link an account or set the owner contact before computing an RMD",
      };
    }

    // Joint Life is not auto-computed — see APP-1169.
    if (
      settings.spouseIsSoleBeneficiary &&
      settings.spouseBirthYear &&
      settings.spouseBirthYear - settings.ownerBirthYear > 10
    ) {
      return {
        error:
          "Joint Life RMD requires manual entry — see IRS Pub 590-B Table II (tracked in APP-1169)",
      };
    }

    return {
      ruleId: "OWN_UNIFORM",
      table: "uniform_iii",
      description: "Uniform Lifetime Table",
      requiresAnnualDistribution: true,
      deadlineYear: null,
    };
  }

  // ── Inherited IRA ──

  if (!settings.originalOwnerBirthYear || !settings.originalOwnerDeathYear) {
    return {
      error: "Inherited IRA requires original owner birth year and death year",
    };
  }

  const deathYear = settings.originalOwnerDeathYear;
  const postSecure = deathYear >= 2020;
  const rmdStartYear = getRMDStartYear(settings.originalOwnerBirthYear);
  const pastRBD = deathYear > rmdStartYear;

  // 1. SPOUSE_AS_OWN
  if (
    settings.beneficiaryType === "spouse" &&
    settings.spouseElection === "treat_as_own"
  ) {
    return {
      ruleId: "SPOUSE_AS_OWN",
      table: "uniform_iii",
      description: "Spouse elected to treat as own — Uniform Lifetime Table",
      requiresAnnualDistribution: true,
      deadlineYear: null,
    };
  }

  // 2. SPOUSE_BENEFICIARY
  if (settings.beneficiaryType === "spouse") {
    return {
      ruleId: "SPOUSE_BENEFICIARY",
      table: "single_life_i",
      description:
        "Spouse beneficiary — Single Life Table, recalculates annually",
      requiresAnnualDistribution: true,
      deadlineYear: null,
    };
  }

  // 3. FIVE_YEAR
  if (settings.beneficiaryType === "entity") {
    return {
      ruleId: "FIVE_YEAR",
      table: "none",
      description: `Non-individual beneficiary — 5-year rule (by 12/31/${deathYear + 5})`,
      requiresAnnualDistribution: false,
      deadlineYear: deathYear + 5,
    };
  }

  // 4. MINOR_CHILD
  if (settings.beneficiaryType === "minor_child") {
    if (settings.beneficiaryBirthYear == null) {
      return {
        error:
          "Minor child beneficiary requires beneficiaryBirthYear to compute the age-21 deadline",
      };
    }
    const age21Year = settings.beneficiaryBirthYear + 21;
    return {
      ruleId: "MINOR_CHILD",
      table: "single_life_i",
      description: `Minor child — stretch to age 21, then 10-year rule (by ${age21Year + 10})`,
      requiresAnnualDistribution: true,
      deadlineYear: age21Year + 10,
    };
  }

  // 5. EDB_STRETCH
  if (
    settings.beneficiaryType === "disabled" ||
    settings.beneficiaryType === "not_much_younger"
  ) {
    return {
      ruleId: "EDB_STRETCH",
      table: "single_life_i",
      description:
        "Eligible Designated Beneficiary — stretch over life expectancy",
      requiresAnnualDistribution: true,
      deadlineYear: null,
    };
  }

  // 6. STRETCH_PRE_SECURE
  if (!postSecure) {
    return {
      ruleId: "STRETCH_PRE_SECURE",
      table: "single_life_i",
      description: "Pre-SECURE Act — stretch over life expectancy",
      requiresAnnualDistribution: true,
      deadlineYear: null,
    };
  }

  // 7. TEN_YEAR_WITH_ANNUAL
  if (pastRBD) {
    return {
      ruleId: "TEN_YEAR_WITH_ANNUAL",
      table: "single_life_i",
      description: `10-year rule with annual RMDs (owner past RBD, by 12/31/${deathYear + 10})`,
      requiresAnnualDistribution: true,
      deadlineYear: deathYear + 10,
    };
  }

  // 8. TEN_YEAR_NO_ANNUAL
  return {
    ruleId: "TEN_YEAR_NO_ANNUAL",
    table: "none",
    description: `10-year rule, no annual RMDs (by 12/31/${deathYear + 10})`,
    requiresAnnualDistribution: false,
    deadlineYear: deathYear + 10,
  };
}
